import { useTRPC } from '@/utils/trpc';
import { AddConditionForm, type AddConditionFormValues } from '@liza/ui/components/records/AddConditionForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useValueSetExpand } from '@liza/ui/hooks/useValueSetExpand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const CONDITION_CODE_VALUE_SET_URL = 'http://hl7.org/fhir/us/core/ValueSet/us-core-condition-code';
const CLINICAL_STATUS_VALUE_SET_URL = 'http://hl7.org/fhir/ValueSet/condition-clinical';

type Props = {
  medplumPatientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddConditionDialog = ({ medplumPatientId, isOpen, onOpenChange }: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const code = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: CONDITION_CODE_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });
  const clinicalStatus = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: CLINICAL_STATUS_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });

  const { mutateAsync: createCondition, isPending: isCreating } = useMutation(
    trpc.patient.condition.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.condition.list.queryKey({ medplumPatientId }),
        });
      },
    }),
  );

  const handleCreate = useCallback(
    async (values: AddConditionFormValues): Promise<void> => {
      try {
        await createCondition({
          resource: {
            category: [
              {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/condition-category',
                    code: 'problem-list-item',
                    display: 'Problem List Item',
                  },
                ],
                text: 'Problem List Item',
              },
            ],
            clinicalStatus: { coding: [values.clinicalStatus] },
            code: { coding: [values.code], text: values.code.display ?? values.code.code },
            note: values.notes ? [{ text: values.notes }] : undefined,
            onsetDateTime: values.onsetDate || undefined,
            recordedDate: new Date().toISOString(),
            resourceType: 'Condition',
            subject: { reference: `Patient/${medplumPatientId}` },
            verificationStatus: {
              coding: [
                {
                  system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
                  code: 'confirmed',
                  display: 'Confirmed',
                },
              ],
            },
          },
        });
        toast.success(t('conditionAdded'));
        onOpenChange(false);
      } catch (error) {
        toast.error(`${t('addConditionFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createCondition, medplumPatientId, onOpenChange, t],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addCondition')}</DialogTitle>
        </DialogHeader>
        <AddConditionForm
          code={code}
          clinicalStatus={clinicalStatus}
          isCreating={isCreating}
          onClose={() => onOpenChange(false)}
          onCreate={handleCreate}
        />
      </DialogContent>
    </Dialog>
  );
};
